import { useMemo, useState } from "react";
import { Copy, HelpCircle } from "lucide-react";
import { buildLawyerQuestions, lawyerQuestionsText } from "@/lib/lawyerQuestions";

// One list per lawyer of the appearances where the note asks a question (or the row is set
// to Query), each with a Copy button for pasting into an email.
export default function LawyerQuestionsPanel({ entries, actions, notes, weekLabel, onJump }) {
  const groups = useMemo(() => buildLawyerQuestions(entries, actions, notes), [entries, actions, notes]);
  const [copied, setCopied] = useState("");
  if (groups.length === 0) return null;

  const total = groups.reduce((sum, group) => sum + group.items.length, 0);
  const copy = async (group) => {
    try {
      await navigator.clipboard.writeText(lawyerQuestionsText(group, weekLabel));
      setCopied(group.lawyer);
      setTimeout(() => setCopied(current => (current === group.lawyer ? "" : current)), 2000);
    } catch {
      setCopied("");
    }
  };

  return (
    <section className="space-y-3 rounded-2xl border border-violet-200 bg-violet-50/60 p-4 print:hidden">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-bold text-violet-950">
          <HelpCircle className="h-4 w-4 text-violet-600" />
          Questions for lawyers ({total})
        </h3>
        <p className="mt-0.5 text-xs text-violet-800">Appearances where your note asks a question (it contains a “?”) or the action is Query, grouped by the lawyer with carriage.</p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {groups.map(group => (
          <div key={group.lawyer} className="rounded-xl border border-violet-200 bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-bold text-slate-900">
                {group.lawyer}
                <span className="ml-2 text-xs font-semibold text-slate-500">{group.items.length} question{group.items.length === 1 ? "" : "s"}</span>
              </p>
              <button
                type="button"
                onClick={() => copy(group)}
                className="flex items-center gap-1.5 rounded-lg border border-violet-300 px-2.5 py-1 text-xs font-bold text-violet-800 hover:bg-violet-50"
                title="Copy this lawyer's questions to paste into an email"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied === group.lawyer ? "Copied!" : "Copy"}
              </button>
            </div>
            <ol className="mt-2 space-y-2">
              {group.items.map(({ entry, note, action }) => (
                <li key={entry.id} className="rounded-lg bg-slate-50 px-2.5 py-2 text-xs">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="font-bold text-slate-800">{entry.date || "—"}</span>
                    <button type="button" onClick={() => onJump?.(entry.id)} className="font-semibold text-blue-700 underline hover:text-blue-900">
                      {entry.client_name || "Client not named"}
                    </button>
                    {entry.court && <span className="text-slate-500">{entry.court}</span>}
                    {action === "Query" && <span className="rounded-full border border-blue-200 bg-blue-50 px-1.5 text-[10px] font-bold text-blue-700">Query</span>}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-slate-800"><strong>Q:</strong> {note || "Set to Query, no note written yet."}</p>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </section>
  );
}
